import { createApi, retry } from '@reduxjs/toolkit/query/react';
import {
  API_SEARCH_PARAMS, SU_API, AUTOCOMPLETE_PARAM, AUTOCOMPLETE_V2_PARAM, WEBINAR_CONFIG, URL_SEARCH_PARAMS,
} from 'src/constants/api';
import {
  LearningPathwayStaticGETResponse,
  LearningPathwayStaticGETRequest,
  ListLearningPathwayRequest,
  ListLearningPathwayResponse,
  ListLearningPathwaysPolyRequest,
  ListLearningPathwaysPolyResponse,
} from 'src/types/api/learningPathwayStatic';
import { LearningPathwayGETRequest, LearningPathwayGETResponse } from 'src/types/api/learningPathway';
import { ContentProgressPOSTRequest, ContentProgressPOSTResponse } from 'src/types/api/contentProgress';
import {
  ListTopicsRequest,
  ListTopicsResponse,
  TopicGETRequest,
  TopicGETResponse,
  FilteredTopicGETRequest,
  FilteredTopicGETResponse,
} from 'src/types/api/topic';
import { SearchResultsResponseApiData, SearchContentRequest } from 'src/types/api/searchResults';
import {
  ListLearningModulesGETRequest,
  ListLearningModulesGETResponse,
  GetLearningModuleGETRequest,
  GetLearningModuleGETResponse,
  ListModuleCardsGETRequest,
  ListModuleCardsGETResponse,
} from 'src/types/api/learningModule';
import { SEARCH_PARAM } from 'src/constants/search';
import { WebinarData } from 'src/types/state/webinar';
import { transformWebinarApiArrayToUI } from 'src/utils/webinarDataTransformer';
import {
  ListWebinarsForUserResponse,
  RegisterUserForWebinarRequest,
  RegisterUserForWebinarResponse,
} from 'src/types/api/webinars';
import { GetSellerContextResponse } from 'src/types/api/sellerContext';
import {
  StartAssessmentPOSTRequest,
  StartAssessmentPOSTResponse,
  SaveAnswerPOSTRequest,
  SaveAnswerPOSTResponse,
  SubmitAssessmentPOSTRequest,
  SubmitAssessmentPOSTResponse,
} from 'src/types/api/assessment';
import {
  GetCustomerCredentialGETRequest,
  GetCustomerCredentialGETResponse,
} from 'src/types/api/credential';
import {
  AutocompleteGETRequest,
  AutocompleteGETResponse,
  AutocompleteV2Request,
  AutocompleteV2Response,
} from '../../../types/api/autocomplete';
import { enhancedBaseQuery } from './baseQuery';

/**
 * Extracts Mons selection params from the current URL and returns them as a query string.
 * This ensures AJAX calls within the widget iframe carry the correct marketplace context.
 * Only applies when the page is loaded under /su-widgets/ path.
 */
const getMonsSelectionParamsString = (): string => {
  if (!window.location.pathname.startsWith('/su-widgets/')) {
    return '';
  }
  const currentParams = new URLSearchParams(window.location.search);
  const monsParams = new URLSearchParams();
  const paramKeys = [
    URL_SEARCH_PARAMS.MONS_SEL_MKID,
    URL_SEARCH_PARAMS.MONS_SEL_MCID,
    URL_SEARCH_PARAMS.IGNORE_SELECTION_CHANGED,
  ];
  paramKeys.forEach((key) => {
    const value = currentParams.get(key);
    if (value) {
      monsParams.append(key, value);
    }
  });
  const result = monsParams.toString();
  return result ? `&${result}` : '';
};

export const suApiSlice = createApi({
  reducerPath: 'suApi',
  baseQuery: retry(enhancedBaseQuery, {
    maxRetries: 2, // default value. This can be granularly configured for each endpoint below.
  }),
  endpoints: (builder) => ({
    getSellerContext: builder.query<GetSellerContextResponse, void>({
      query: () => ({
        url: SU_API.SELLER_CONTEXT,
        method: 'GET',
      }),
      // Cache seller context for 5 minutes
      // While sessionId is persistent across login/logout, customerId and merchantId
      // change when users authenticate, so we use a short cache duration
      // to ensure fresh authentication data while still providing performance benefits
      keepUnusedDataFor: 300, // 5 minutes cache
    }),
    getLearningPathwayStatic: builder.query<LearningPathwayStaticGETResponse, LearningPathwayStaticGETRequest>({
      query: (payload: LearningPathwayStaticGETRequest) => {
        const params = new URLSearchParams();
        params.append(API_SEARCH_PARAMS.LEARNING_PATHWAY_ID, payload.learningPathwayId);
        params.append(API_SEARCH_PARAMS.CLIENT_ID, payload.clientId);
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.LEARNING_PATHWAY_STATIC}?${params.toString()}`,
          method: 'GET',
        };
      },
      // @todo: Temporary patch to prevent other API calls from overwriting detailed data
      // SIM: Migrate to proper preview/full data structure to allow intelligent caching https://sim.amazon.com/issues/SU-TD-111
      // Remove this once preview/full pattern is implemented
      keepUnusedDataFor: 0,
    }),
    getLearningPathway: builder.query<LearningPathwayGETResponse, LearningPathwayGETRequest>({
      query: (payload: LearningPathwayGETRequest) => {
        const params = new URLSearchParams();
        params.append(API_SEARCH_PARAMS.LEARNING_PATHWAY_ID, payload.learningPathwayId);
        params.append(API_SEARCH_PARAMS.CLIENT_ID, payload.clientId);
        if (payload.metadataOnly) {
          params.append(API_SEARCH_PARAMS.METADATA_ONLY, 'true');
        }
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.LEARNING_PATHWAY}?${params.toString()}${getMonsSelectionParamsString()}`,
          method: 'GET',
        };
      },
      // LP content is static within a session; cache for 5 min so back/repeat navigation
      // reuses it instead of re-running the fetch->render->image-discovery waterfall.
      keepUnusedDataFor: 300,
    }),
    getTopic: builder.query<TopicGETResponse, TopicGETRequest>({
      query: (payload: TopicGETRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.TOPIC_ID]: payload.topicId,
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.TOPIC}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    getFilteredTopic: builder.query<FilteredTopicGETResponse, FilteredTopicGETRequest>({
      query: (payload: FilteredTopicGETRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.TOPIC_ID]: payload.topicId,
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.TOPIC_FILTER}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    listTopics: builder.query<ListTopicsResponse, ListTopicsRequest>({
      query: (payload: ListTopicsRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.LIST_TOPICS}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    listLearningPathways: builder.query<ListLearningPathwayResponse, ListLearningPathwayRequest>({
      query: (payload: ListLearningPathwayRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.LIST_LEARNING_PATHWAYS}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    listLearningPathwaysPoly: builder.query<ListLearningPathwaysPolyResponse, ListLearningPathwaysPolyRequest>({
      query: (payload: ListLearningPathwaysPolyRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
          [API_SEARCH_PARAMS.CAROUSEL_ID]: payload.carouselId,
        });
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.LIST_LEARNING_PATHWAYS_POLY}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    listLearningModules: builder.query<ListLearningModulesGETResponse, ListLearningModulesGETRequest>({
      query: (payload: ListLearningModulesGETRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.QUERY_TYPE]: payload.queryPattern.queryType,
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });

        // Add parameters based on query type
        if (payload.queryPattern.subtopicQuery) {
          params.append(API_SEARCH_PARAMS.SUBTOPIC_ID, payload.queryPattern.subtopicQuery.subtopicId);
        }

        if (payload.queryPattern.tagsQuery) {
          // Add multiple tagId parameters for TAGS query type
          payload.queryPattern.tagsQuery.tagIds.forEach((tagId) => {
            params.append(API_SEARCH_PARAMS.TAG_ID, tagId);
          });
        }

        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);

        return {
          url: `${SU_API.LIST_LEARNING_MODULES}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    listModuleCards: builder.query<ListModuleCardsGETResponse, ListModuleCardsGETRequest>({
      query: (payload: ListModuleCardsGETRequest) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.QUERY_TYPE]: payload.queryPattern.queryType,
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });

        // Add parameters based on query type
        if (payload.queryPattern.subtopicQuery) {
          params.append(API_SEARCH_PARAMS.SUBTOPIC_ID, payload.queryPattern.subtopicQuery.subtopicId);
        }

        if (payload.queryPattern.tagsQuery) {
          // Add multiple tagId parameters for TAGS query type
          payload.queryPattern.tagsQuery.tagIds.forEach((tagId) => {
            params.append(API_SEARCH_PARAMS.TAG_ID, tagId);
          });
        }

        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);

        return {
          url: `${SU_API.LIST_MODULE_CARDS}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    getLearningModule: builder.query<GetLearningModuleGETResponse, GetLearningModuleGETRequest>({
      query: (payload: GetLearningModuleGETRequest) => {
        const params = new URLSearchParams();
        params.append(API_SEARCH_PARAMS.MODULE_ID, payload.moduleId);
        params.append(API_SEARCH_PARAMS.CLIENT_ID, payload.clientId);
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.LEARNING_MODULE}?${params.toString()}${getMonsSelectionParamsString()}`,
          method: 'GET',
        };
      },
    }),
    addContentProgress: builder.mutation<ContentProgressPOSTResponse, ContentProgressPOSTRequest>({
      query: (payload: ContentProgressPOSTRequest) => {
        const monsParams = getMonsSelectionParamsString();
        return {
          url: `${SU_API.CONTENT_PROGRESS}${monsParams ? `?${monsParams.substring(1)}` : ''}`,
          method: 'POST',
          body: JSON.stringify(payload),
        };
      },
    }),
    searchContent: builder.query<SearchResultsResponseApiData, SearchContentRequest>({
      query: ({
        query, pageNumber, clientId, selLocale,
      }) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.QUERY]: query,
          [API_SEARCH_PARAMS.PAGE_NUMBER]: pageNumber.toString(),
          [API_SEARCH_PARAMS.PAGE_SIZE]: SEARCH_PARAM.PAGE_SIZE.toString(),
          [API_SEARCH_PARAMS.CLIENT_ID]: clientId,
        });
        if (selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, selLocale);

        return {
          url: `${SU_API.SEARCH}?${params.toString()}`,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 600,
    }),
    autocomplete: builder.query<AutocompleteGETResponse, AutocompleteGETRequest>({
      query: (payload: AutocompleteGETRequest) => {
        const params = new URLSearchParams({
          prefix: payload.prefix,
        });

        params.append('maxResults', (payload.maxResults ?? AUTOCOMPLETE_PARAM.MAX_RESULTS).toString());

        return {
          url: `${SU_API.AUTOCOMPLETE}?${params.toString()}`,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 300,
    }),
    autocompleteV2: builder.query<AutocompleteV2Response, AutocompleteV2Request>({
      query: (payload: AutocompleteV2Request) => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.QUERY]: payload.query,
          maxSuggestions: (payload.maxSuggestions ?? AUTOCOMPLETE_V2_PARAM.MAX_SUGGESTIONS).toString(),
          maxModules: (payload.maxModules ?? AUTOCOMPLETE_V2_PARAM.MAX_MODULES).toString(),
          [API_SEARCH_PARAMS.CLIENT_ID]: payload.clientId,
        });
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);

        return {
          url: `${SU_API.AUTOCOMPLETE_V2}?${params.toString()}`,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 300,
    }),
    listWebinarsForUser: builder.query<{ webinars: WebinarData[] }, void>({
      query: () => {
        const params = new URLSearchParams({
          [API_SEARCH_PARAMS.WEBINAR_CLIENT_ID]: WEBINAR_CONFIG.CLIENT_ID,
        });

        return {
          url: `${SU_API.LIST_WEBINARS}?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: ListWebinarsForUserResponse) => {
        // Transform API response using our transformer utility
        const transformedWebinars = transformWebinarApiArrayToUI(response.webinars);
        return {
          webinars: transformedWebinars,
        };
      },
      keepUnusedDataFor: 300,
    }),
    registerUserForWebinar: builder.mutation<RegisterUserForWebinarResponse, RegisterUserForWebinarRequest>({
      query: (payload: RegisterUserForWebinarRequest) => {
        return {
          url: `${SU_API.REGISTER_WEBINAR}`,
          method: 'POST',
          body: JSON.stringify(payload),
        };
      },
    }),
    getCustomerCredential: builder.query<GetCustomerCredentialGETResponse, GetCustomerCredentialGETRequest>({
      query: (payload: GetCustomerCredentialGETRequest) => {
        const params = new URLSearchParams();
        params.append(API_SEARCH_PARAMS.CREDENTIAL_ID, payload.credentialId);
        params.append(API_SEARCH_PARAMS.CLIENT_ID, payload.clientId);
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.CUSTOMER_CREDENTIAL}?${params.toString()}${getMonsSelectionParamsString()}`,
          method: 'GET',
        };
      },
    }),
    startAssessment: builder.mutation<StartAssessmentPOSTResponse, StartAssessmentPOSTRequest>({
      query: (payload: StartAssessmentPOSTRequest) => {
        const params = new URLSearchParams();
        params.append(API_SEARCH_PARAMS.CLIENT_ID, payload.clientId);
        if (payload.selLocale) params.append(API_SEARCH_PARAMS.SEL_LOCALE, payload.selLocale);
        return {
          url: `${SU_API.ASSESSMENT_START}?${params.toString()}${getMonsSelectionParamsString()}`,
          method: 'POST',
          body: JSON.stringify({ credentialId: payload.credentialId }),
        };
      },
    }),
    saveAnswer: builder.mutation<SaveAnswerPOSTResponse, SaveAnswerPOSTRequest>({
      query: (payload: SaveAnswerPOSTRequest) => {
        const monsParams = getMonsSelectionParamsString();
        return {
          url: `${SU_API.ASSESSMENT_ANSWER}${monsParams ? `?${monsParams.substring(1)}` : ''}`,
          method: 'POST',
          body: JSON.stringify(payload),
        };
      },
    }),
    submitAssessment: builder.mutation<SubmitAssessmentPOSTResponse, SubmitAssessmentPOSTRequest>({
      query: (payload: SubmitAssessmentPOSTRequest) => {
        const monsParams = getMonsSelectionParamsString();
        return {
          url: `${SU_API.ASSESSMENT_SUBMIT}${monsParams ? `?${monsParams.substring(1)}` : ''}`,
          method: 'POST',
          body: JSON.stringify(payload),
        };
      },
    }),
  }),
});

export const {
  useGetSellerContextQuery,
  useGetLearningPathwayStaticQuery,
  useGetLearningPathwayQuery,
  useGetTopicQuery,
  useGetFilteredTopicQuery,
  useListTopicsQuery,
  useLazyListTopicsQuery,
  useListLearningPathwaysQuery,
  useListLearningPathwaysPolyQuery,
  useListLearningModulesQuery,
  useListModuleCardsQuery,
  useGetLearningModuleQuery,
  useAddContentProgressMutation,
  useSearchContentQuery,
  useLazyAutocompleteQuery,
  useLazyAutocompleteV2Query,
  useListWebinarsForUserQuery,
  useRegisterUserForWebinarMutation,
  useGetCustomerCredentialQuery,
  useStartAssessmentMutation,
  useSaveAnswerMutation,
  useSubmitAssessmentMutation,
} = suApiSlice;
